import * as React from "react";
import { connect } from "react-redux";
import type { Dispatch } from "redux";
import ErrorComponent from "@/modules/shared/ui/ErrorComponent";
import { UPDATE_LINES_ORDERS_ACTION_NAME } from "@/modules/pg/shared/planningTable/reducers/linesOrders";
import { SET_ERROR } from "@/modules/pg/shared/planningTable/reducers/planningTable";
import { translate } from "@/modules/shared/IntlProviderWrapper";

import type { ActionTypes, ReduxState } from "@/modules/pg/shared/planningTable/reducers/reducers";
import { deepClone } from "@/lib/util";

type OwnProps = {};
type Props = OwnProps & ReduxProps;

type State = {
    event_msg: string | null;
    onCancel: () => void;
};

export class ErrorHandler extends React.Component<Props, State> {
    nodeRef = React.createRef<HTMLDivElement>();
    errorHandlerListener: ((e: Event) => void) | null = null;

    state: State = {
        event_msg: null,
        onCancel: () => {},
    };

    componentDidMount() {
        if (this.nodeRef && this.nodeRef.current) {
            this.errorHandlerListener = (e: Event) => this.onErrorEventHandler(e);
            this.nodeRef.current.addEventListener("error-handler-event", this.errorHandlerListener);
        }
    }

    onErrorEventHandler = (event: Event) => {
        const detail = event instanceof CustomEvent ? event.detail : {};
        this.setState({ event_msg: detail.msg, onCancel: detail.onCancel });
    };

    componentWillUnmount() {
        if (this.nodeRef && this.nodeRef.current && this.errorHandlerListener) {
            this.nodeRef.current.removeEventListener("error-handler-event", this.errorHandlerListener);
        }
    }

    onClickRetry = () => {
        this.props.reduxDispatch({ type: SET_ERROR, data: null });
        this.props.reduxDispatch({
            type: UPDATE_LINES_ORDERS_ACTION_NAME,
            data: deepClone(this.props.lines_orders),
        });
    };

    retrySimulationEl = () => {
        if (this.props.report_loading) {
            return null;
        }

        const style_close: React.CSSProperties = {
            top: "0px",
            float: "right",
            cursor: "pointer",
            marginBottom: "5px",
        };

        return (
            <div onClick={this.onClickRetry} style={style_close}>
                <i className="fas fa-redo"></i>
            </div>
        );
    };

    onCancel = () => {
        if (this.state.onCancel) {
            this.state.onCancel();
        }
        this.setState({ event_msg: "" });
    };

    getCancelEl = (): React.ReactNode => {
        return null;
    };

    onClose = () => {
        this.setState({ event_msg: "" });
        this.props.reduxDispatch({ type: SET_ERROR, data: null });
    };

    errorMsg = () => {
        if (this.props.error) {
            return (
                <span title={this.props.error}>
                    {translate(
                        "common.simulation_error",
                        "Please try again. An error occured while processing simulation data."
                    )}
                </span>
            );
        }
        return null;
    };

    render() {
        let error_el: React.ReactNode = null;
        if (this.state.event_msg && this.props.report_loading) {
            error_el = (
                <div className="planning-details-error-sticky" style={{ display: "flex" }}>
                    <div
                        className="alert alert-warning mb-0 rounded-0"
                        style={{
                            width: "100%",
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            wordBreak: "break-word",
                        }}
                    >
                        {this.state.event_msg} {this.getCancelEl()}
                    </div>
                </div>
            );
        } else if (this.props.error) {
            error_el = (
                <div className="planning-details-error-sticky">
                    <ErrorComponent
                        className="mb-0 rounded-0"
                        msg={this.errorMsg()}
                        type="warning"
                        onlyDiv={true}
                        is_closable={true}
                        onClose={this.onClose}
                    />
                </div>
            );
        }

        return (
            <div id="error-handler" ref={this.nodeRef}>
                {error_el}
            </div>
        );
    }
}

const mapState = (state: ReduxState) => {
    const report = state.gantt_chart_report;
    const planning_table = state.gantt_chart_planning_table;
    const lines_orders = state.gantt_chart_lines_orders.lines_orders;

    return {
        report_loading: report.report_loading,
        lines_orders,
        error: planning_table.error,
    };
};

const mapDispatch = (dispatch: Dispatch<ActionTypes>) => ({ reduxDispatch: dispatch });

type ReduxProps = ReturnType<typeof mapState> & ReturnType<typeof mapDispatch>;

export default connect<ReturnType<typeof mapState>, ReturnType<typeof mapDispatch>, OwnProps, ReduxState>(
    mapState,
    mapDispatch
)(ErrorHandler);
